/**
 * Ask how the chart should look before downloading it. The slide-over hands
 * the answers back through the `export-chart` event, which the chart picks up
 * and passes to {@link exportChartImage}. The filename leaves the extension
 * off: Highcharts adds it for the type.
 *
 * Flourish charts draw their own snapshot, which none of the options apply
 * to, so they download straight away.
 */
export function exportChart(chartId, type, filename = null) {
    if (document.getElementById(chartId)?.closest('.flourish-chart')) {
        window.dispatchEvent(new CustomEvent('export-chart', {detail: {type, chartId, filename}}));

        return;
    }

    window.Livewire.dispatch('slide-over.open', {
        component: 'slide-overs.export-chart-image',
        arguments: {chartId, format: type, filename},
    });
}

export function downloadSvgChart(id, filename = null) {
    exportChart(id, 'image/svg+xml', filename);
}

export function downloadPngChart(id, filename = null) {
    exportChart(id, 'image/png', filename);
}

export function downloadJpgChart(id, filename = null) {
    exportChart(id, 'image/jpeg', filename);
}

/**
 * The handler every Highcharts component answers an export-chart event with:
 * download the chart as an image, drawn the way the export slide-over asked
 * for. Anything not asked for keeps the chart's own exporting options.
 * Without a filename Highcharts names the download after the chart title,
 * which the theme leaves empty, so everything would arrive as "chart.png".
 */
export function exportChartImage(chart, {chartId, type, filename = null, options = {}}) {
    if (!chart || chart.renderTo.id !== chartId) {
        return;
    }

    const exportingOptions = {type, ...(filename ? {filename} : {})};
    const chartOptions = {};

    if (options.width && options.height) {
        exportingOptions.sourceWidth = options.width;
        exportingOptions.sourceHeight = options.height;
    }

    if (options.scale) {
        exportingOptions.scale = options.scale;
    }

    const chartChanges = {};

    if (options.transparent) {
        chartChanges.backgroundColor = 'transparent';
    }

    if (options.branding) {
        const colors = options.branding.colors;

        // Charts often colour their series themselves, so the palette has to
        // be set on each series as well as on the chart.
        chartOptions.colors = colors;
        chartOptions.series = chart.series
            .filter((series) => !series.options.isInternal)
            .map((series, index) => ({color: colors[index % colors.length]}));

        chartChanges.style = {fontFamily: `"${options.branding.font}", InterVariable, Inter, Arial, sans-serif`};
    }

    if (Object.keys(chartChanges).length) {
        chartOptions.chart = chartChanges;
    }

    if (options.title === false) {
        chartOptions.title = {text: null};
        chartOptions.subtitle = {text: null};
    }

    if (options.legend === false) {
        chartOptions.legend = {enabled: false};
    }

    if (options.source === false) {
        chartOptions.credits = {enabled: false};
    }

    if (options.dataLabels === false) {
        chartOptions.plotOptions = {series: {dataLabels: {enabled: false}}};
    }

    chart.exportChartLocal(exportingOptions, chartOptions);
}

export function addQuadrant(chart, name, x1, y1, x2, y2, attr = {}) {
    maybeDestroyObject(chart, name);

    const x1Pixels = chart.xAxis[0].toPixels(x1, false);
    const y1Pixels = chart.yAxis[0].toPixels(y1, false);
    const x2Pixels = chart.xAxis[0].toPixels(x2, false);
    const y2Pixels = chart.yAxis[0].toPixels(y2, false);

    chart[name] = chart.renderer
        .rect(x1Pixels, y2Pixels, (x2Pixels - x1Pixels), (y1Pixels - y2Pixels), 1)
        .attr({
            ...{
                fill: '#000050',
                opacity: 0.2,
                zIndex: 0
            }, ...attr
        })
        .add();
}

export function addText(chart, key, label, x, y, css = {}, attr = {}) {
    maybeDestroyObject(chart, key);

    const xPixels = chart.xAxis[0].toPixels(x, false);
    const yPixels = chart.yAxis[0].toPixels(y, false);

    chart[key] = chart.renderer.text(label, xPixels, yPixels)
        .css({
            ...{
                fontWeight: 'bold',
                fontSize: '13px',
                color: '#666666',
                zIndex: 5,
            }, ...css
        })
        .attr({
            ...{
                align: 'center'
            },
            ...attr
        })
        .add();
}

export function addLine(chart, key, x1, y1, x2, y2, attr = {}) {
    maybeDestroyObject(chart, key);

    const x1Pixels = chart.xAxis[0].toPixels(x1, false);
    const y1Pixels = chart.yAxis[0].toPixels(y1, false);
    const x2Pixels = chart.xAxis[0].toPixels(x2, false);
    const y2Pixels = chart.yAxis[0].toPixels(y2, false);

    chart[key] = chart.renderer
        .path(['M', x1Pixels, y1Pixels, 'L', x2Pixels, y2Pixels])
        .attr({
            ...{
                'stroke-width': 1,
                stroke: '#eee',
                zIndex: 2
            }, ...attr
        })
        .add();
}

export function maybeDestroyObject(chart, key) {
    if (chart[key]) {
        chart[key].destroy();
    }
}
